import { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Card,
  CardContent,
  CardActionArea,
  Avatar,
  Chip,
  Stack,
  Alert,
  Checkbox,
  Divider,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  MenuItem,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  Psychology as PsychologyIcon,
  Diversity3 as Diversity3Icon,
  Public as PublicIcon,
  FamilyRestroom as FamilyRestroomIcon,
  MedicalServices as MedicalServicesIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import patient360Data from '../../mocks/patient360Data.json';
import {
  saveGapAnalysis,
  type GapDomain,
  type GapSeverity,
  type GapQuestion,
} from '../../utils/patientGapAnalysisStore';
import { suggestGaps } from '../../utils/gapAnalysis/questionGeneration';

const steps = [
  'Select Patient',
  'Review Patient Data',
  'Detected Gaps',
  'Review Questions',
  'Publish',
];

type Patient = typeof patient360Data.assignedPatients[0];

const DOMAIN_META: Record<GapDomain, { label: string; color: 'error' | 'warning' | 'info' | 'success' | 'secondary'; Icon: React.ElementType }> = {
  CLINICAL:   { label: 'Clinical',   color: 'error',     Icon: MedicalServicesIcon },
  BEHAVIORAL: { label: 'Behavioral', color: 'warning',   Icon: PsychologyIcon },
  CULTURAL:   { label: 'Cultural',   color: 'info',      Icon: Diversity3Icon },
  FAMILY:     { label: 'Family',     color: 'success',   Icon: FamilyRestroomIcon },
  SDOH:       { label: 'SDOH',       color: 'secondary', Icon: PublicIcon },
};

const SEVERITY_COLOR: Record<GapSeverity, 'error' | 'warning' | 'default'> = {
  HIGH:   'error',
  MEDIUM: 'warning',
  LOW:    'default',
};

const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

export const QuestionsGapsPage = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [questions, setQuestions] = useState<GapQuestion[]>([]);
  const [domainFilter, setDomainFilter] = useState<GapDomain | 'ALL'>('ALL');

  const handleNext = () => setActiveStep((s) => s + 1);
  const handleBack = () => setActiveStep((s) => s - 1);

  const handleReset = () => {
    setActiveStep(0);
    setSelectedPatient(null);
    setQuestions([]);
    setDomainFilter('ALL');
  };

  const handleGenerateGaps = () => {
    if (!selectedPatient) return;
    setQuestions(suggestGaps(selectedPatient.id));
    handleNext();
  };

  const toggleQuestion = (id: string, included: boolean) => {
    setQuestions((prev) => prev.map((q) => q.id === id ? { ...q, included } : q));
  };

  const updateQuestionText = (id: string, text: string) => {
    setQuestions((prev) => prev.map((q) => q.id === id ? { ...q, question: text } : q));
  };

  const updateQuestionDomain = (id: string, domain: GapDomain) => {
    setQuestions((prev) => prev.map((q) => q.id === id ? { ...q, domain } : q));
  };

  const handlePublish = () => {
    if (!selectedPatient) return;
    saveGapAnalysis({
      id: `gap-analysis-${genId()}`,
      patientId: selectedPatient.id,
      patientName: `${selectedPatient.firstName} ${selectedPatient.lastName}`,
      createdDate: new Date().toISOString(),
      questions: questions.filter((q) => q.included),
    });
    handleNext();
  };

  const isStepValid = () => {
    switch (activeStep) {
      case 0: return selectedPatient !== null;
      case 1: return true;
      case 2: return questions.some((q) => q.included);
      case 3: return questions.some((q) => q.included);
      case 4: return true;
      default: return false;
    }
  };

  const filteredQuestions = domainFilter === 'ALL'
    ? questions
    : questions.filter((q) => q.domain === domainFilter);

  const gapGroups = Array.from(
    questions.reduce((map, q) => {
      if (!map.has(q.gapKey)) {
        map.set(q.gapKey, { gapKey: q.gapKey, gapLabel: q.gapLabel, domain: q.domain, severity: q.severity, count: 0, includedCount: 0 });
      }
      const entry = map.get(q.gapKey)!;
      entry.count += 1;
      if (q.included) entry.includedCount += 1;
      return map;
    }, new Map<string, { gapKey: string; gapLabel: string; domain: GapDomain; severity: GapSeverity; count: number; includedCount: number }>())
    .values()
  );

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>Select a patient to analyse gaps</Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {patient360Data.assignedPatients.map((patient) => (
                <Grid item xs={12} sm={6} md={4} key={patient.id}>
                  <Card sx={{ border: selectedPatient?.id === patient.id ? '2px solid' : '1px solid', borderColor: selectedPatient?.id === patient.id ? 'primary.main' : 'divider' }}>
                    <CardActionArea onClick={() => setSelectedPatient(patient)} sx={{ p: 2 }}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ bgcolor: patient.riskLevel === 'HIGH' ? 'error.main' : patient.riskLevel === 'MODERATE' ? 'warning.main' : 'success.main' }}>
                          <PersonIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>{patient.firstName} {patient.lastName}</Typography>
                          <Stack direction="row" spacing={0.5}>
                            <Chip label={`Risk: ${patient.riskLevel}`} size="small" color={patient.riskLevel === 'HIGH' ? 'error' : patient.riskLevel === 'MODERATE' ? 'warning' : 'success'} />
                            <Chip label={`${patient.dataCompleteness}% data`} size="small" variant="outlined" />
                          </Stack>
                        </Box>
                      </Stack>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        );

      case 1: {
        type PatDetails = {
          cgmData: { data: { timeInRange: { target: number; above: number; below: number }; averageGlucose: { value: number; unit: string } } };
          nutritionData: { data: { mealLoggingCompliance: string; macroDistribution: { carbs: number; protein: number; fat: number } } };
          socialDeterminants: { data: { foodSecurity: { status: string }; employment: { status: string } } };
          clinicalInfo: { data: { primaryDiagnosis: string; conditions: Array<{ name: string }> } };
        };
        const details = (patient360Data.patientDetails as unknown as Record<string, PatDetails>)[selectedPatient!.id];
        return (
          <Box>
            <Typography variant="h6" gutterBottom>Patient Data Summary — {selectedPatient?.firstName} {selectedPatient?.lastName}</Typography>
            <Alert severity="info" sx={{ mb: 2 }} icon={<AutoAwesomeIcon />}>
              Review available patient data. The gap engine will use this to recommend focused questions.
            </Alert>
            {details ? (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card><CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>CGM Summary</Typography>
                    <Typography variant="body2">Time in Range: <strong>{details.cgmData.data.timeInRange.target}%</strong></Typography>
                    <Typography variant="body2">Avg Glucose: <strong>{details.cgmData.data.averageGlucose.value} {details.cgmData.data.averageGlucose.unit}</strong></Typography>
                    <Typography variant="body2">Above: {details.cgmData.data.timeInRange.above}% · Below: {details.cgmData.data.timeInRange.below}%</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card><CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Clinical</Typography>
                    <Typography variant="body2">Diagnosis: <strong>{details.clinicalInfo.data.primaryDiagnosis}</strong></Typography>
                    <Typography variant="body2">Conditions: {details.clinicalInfo.data.conditions.map((c: { name: string }) => c.name).join(', ')}</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card><CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Nutrition</Typography>
                    <Typography variant="body2">Meal Compliance: <strong>{details.nutritionData.data.mealLoggingCompliance}</strong></Typography>
                    <Typography variant="body2">Carbs: {details.nutritionData.data.macroDistribution.carbs}% · Protein: {details.nutritionData.data.macroDistribution.protein}% · Fat: {details.nutritionData.data.macroDistribution.fat}%</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card><CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Social Determinants</Typography>
                    <Typography variant="body2">Food Security: <strong>{details.socialDeterminants.data.foodSecurity.status}</strong></Typography>
                    <Typography variant="body2">Employment: {details.socialDeterminants.data.employment.status}</Typography>
                  </CardContent></Card>
                </Grid>
              </Grid>
            ) : (
              <Alert severity="warning">Full patient details not available in mock data — gap analysis will use all templates.</Alert>
            )}
          </Box>
        );
      }

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>Detected Knowledge Gaps</Typography>
            <Alert severity="info" sx={{ mb: 2 }} icon={<AutoAwesomeIcon />}>
              HIGH severity gaps are pre-selected. Toggle which gaps to include — each gap generates targeted questions.
            </Alert>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Domain</TableCell>
                    <TableCell>Gap</TableCell>
                    <TableCell>Severity</TableCell>
                    <TableCell>Questions</TableCell>
                    <TableCell>Include All</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from(gapGroups).map((group) => {
                    const groupQs = questions.filter((q) => q.gapKey === group.gapKey);
                    const allIncluded = groupQs.every((q) => q.included);
                    const anyIncluded = groupQs.some((q) => q.included);
                    const meta = DOMAIN_META[group.domain];
                    return (
                      <TableRow key={group.gapKey}>
                        <TableCell><Chip size="small" label={meta.label} color={meta.color} /></TableCell>
                        <TableCell><Typography variant="body2" fontWeight={600}>{group.gapLabel}</Typography></TableCell>
                        <TableCell><Chip size="small" label={group.severity} color={SEVERITY_COLOR[group.severity]} /></TableCell>
                        <TableCell>{group.count}</TableCell>
                        <TableCell>
                          <Checkbox
                            checked={allIncluded}
                            indeterminate={anyIncluded && !allIncluded}
                            onChange={(e) => setQuestions((prev) => prev.map((q) => q.gapKey === group.gapKey ? { ...q, included: e.target.checked } : q))}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6">Review & Customise Questions</Typography>
              <TextField select size="small" label="Filter by domain" value={domainFilter} onChange={(e) => setDomainFilter(e.target.value as GapDomain | 'ALL')} sx={{ minWidth: 180 }}>
                <MenuItem value="ALL">All Domains</MenuItem>
                {(Object.keys(DOMAIN_META) as GapDomain[]).map((d) => (
                  <MenuItem key={d} value={d}>{DOMAIN_META[d].label}</MenuItem>
                ))}
              </TextField>
            </Stack>
            <Alert severity="info" sx={{ mb: 2 }}>Edit question wording, change domain, or exclude questions before publishing.</Alert>
            <Stack spacing={2}>
              {filteredQuestions.map((q) => {
                const meta = DOMAIN_META[q.domain];
                return (
                  <Paper key={q.id} variant="outlined" sx={{ p: 2, opacity: q.included ? 1 : 0.5 }}>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <Checkbox checked={q.included} onChange={(e) => toggleQuestion(q.id, e.target.checked)} sx={{ mt: -0.5 }} />
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" spacing={1} mb={1} flexWrap="wrap">
                          <Chip size="small" label={meta.label} color={meta.color} />
                          <Chip size="small" label={q.gapLabel} variant="outlined" />
                          <Chip size="small" label={q.severity} color={SEVERITY_COLOR[q.severity]} />
                        </Stack>
                        <TextField fullWidth size="small" multiline value={q.question} onChange={(e) => updateQuestionText(q.id, e.target.value)} disabled={!q.included} sx={{ mb: 1 }} />
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>{q.rationale}</Typography>
                          <TextField select size="small" label="Domain" value={q.domain} onChange={(e) => updateQuestionDomain(q.id, e.target.value as GapDomain)} disabled={!q.included} sx={{ minWidth: 140 }}>
                            {(Object.keys(DOMAIN_META) as GapDomain[]).map((d) => (
                              <MenuItem key={d} value={d}>{DOMAIN_META[d].label}</MenuItem>
                            ))}
                          </TextField>
                        </Stack>
                      </Box>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Box>
        );

      case 4: {
        const includedQs = questions.filter((q) => q.included);
        const byDomain = (Object.keys(DOMAIN_META) as GapDomain[])
          .map((d) => ({ domain: d, qs: includedQs.filter((q) => q.domain === d) }))
          .filter((g) => g.qs.length > 0);
        return (
          <Box>
            <Typography variant="h6" gutterBottom>Review & Publish Gap Analysis</Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>{includedQs.length}</strong> questions across <strong>{byDomain.length}</strong> domains will be published for <strong>{selectedPatient?.firstName} {selectedPatient?.lastName}</strong>.
            </Alert>
            <Stack spacing={2}>
              {byDomain.map(({ domain, qs }) => {
                const meta = DOMAIN_META[domain];
                return (
                  <Card key={domain}><CardContent>
                    <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                      <Chip label={meta.label} color={meta.color} size="small" />
                      <Typography variant="subtitle2">{qs.length} question{qs.length > 1 ? 's' : ''}</Typography>
                    </Stack>
                    <Divider sx={{ mb: 1 }} />
                    <Stack spacing={0.5}>
                      {qs.map((q) => <Typography key={q.id} variant="body2">• {q.question}</Typography>)}
                    </Stack>
                  </CardContent></Card>
                );
              })}
            </Stack>
          </Box>
        );
      }

      case 5:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>Gap Analysis Published!</Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {questions.filter((q) => q.included).length} questions for {selectedPatient?.firstName} {selectedPatient?.lastName} are now visible in the Patient 360° Gap Analysis tab.
            </Typography>
            <Button variant="contained" onClick={handleReset}>Analyse Another Patient</Button>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Patient Data Gap Analysis</Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Generate personalised questions to fill clinical, behavioural, cultural, family, and SDOH knowledge gaps
      </Typography>
      <Paper sx={{ p: 3, mt: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>
        {getStepContent(activeStep)}
        {activeStep !== 5 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button disabled={activeStep === 0} onClick={handleBack}>Back</Button>
            <Button
              variant="contained"
              disabled={!isStepValid()}
              onClick={activeStep === 1 ? handleGenerateGaps : activeStep === 4 ? handlePublish : handleNext}
            >
              {activeStep === 1 ? 'Analyse Gaps' : activeStep === 4 ? 'Publish Gap Analysis' : 'Next'}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
